'use client';

import { useState } from 'react';

import { twMerge } from 'tailwind-merge';
import { useAnalytics } from '@domain/analytics/AnalyticsContext';
import { trackEvent } from '@domain/analytics/trackEvent';
import { SpeciesTopBaitNodeFields } from '../../generated/graphql';
import { AppDownloadModal } from '../AppDownloadModal/AppDownloadModal';
import { Button } from '../Clickables/Buttons/Button';
import { Heading } from '../Heading/Heading';
import { Text } from '../Text/Text';
import { TopBaitsEntry } from './components/TopBaitsEntry';
import { TopBaitsSectionCTA } from './components/TopBaitsSectionCTA';

interface IProps {
  className?: string;
  topBaits?: SpeciesTopBaitNodeFields[];
  sourceId?: string;
  id?: string;
  headingText?: string;
  appDownloadModalText?: string;
}

export const TopBaitsSection = ({
  className,
  topBaits,
  sourceId,
  id = 'top-baits-section',
  headingText = 'Best baits',
  appDownloadModalText,
}: IProps) => {
  const { currentPage } = useAnalytics();
  const [isAppDownloadModalOpen, setIsAppDownloadModalOpen] = useState(false);
  const hasTopBaits = topBaits && topBaits.length > 0;

  const handleCTAClick = () => {
    trackEvent({
      name: 'mykiss_cta_button_clicked',
      properties: {
        source: 'TopBaitsSection',
        source_id: sourceId,
        type: 'downloadModal',
        page: currentPage,
      },
    });
    setIsAppDownloadModalOpen(true);
  };

  return (
    <div
      id={id}
      className={twMerge(
        'sm:bg-lake-fog scroll-mt-(--navigation-scrolltop-offset-mobile) sm:scroll-mt-(--navigation-scrolltop-offset) sm:rounded-2xl',
        className,
      )}
    >
      <Heading className="mx-auto text-left" variant="sm">
        {headingText}
      </Heading>
      <Text className="my-5">
        BiteGuide combines your real-time weather, water conditions, and target species to suggest
        lures and colors that&apos;ll work right now. Built on millions of real catches from the
        world&apos;s largest fishing community.
      </Text>

      <div className="flex flex-col">
        <TopBaitsSectionCTA sourceId={sourceId} hasTopBaits={hasTopBaits} />
        {topBaits?.map(({ productUnit, numberOfCatches, position }) => {
          return (
            <TopBaitsEntry
              key={`species-${productUnit.id}`}
              productUnit={productUnit}
              numberOfCatches={numberOfCatches}
              position={position}
              onClick={handleCTAClick}
            />
          );
        })}
        {hasTopBaits && (
          <Button size="sm" className="my-2 p-2" variant="textPrimary" onClick={handleCTAClick}>
            Show more baits
          </Button>
        )}
      </div>
      <AppDownloadModal
        appDownloadSource="TopBaitsSection"
        appDownloadSourceId={sourceId}
        modalHeader={appDownloadModalText}
        isModalOpen={isAppDownloadModalOpen}
        onCloseModal={() => setIsAppDownloadModalOpen(false)}
      />
    </div>
  );
};
